import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Users, Target, TrendingUp, Settings } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { SavingsGroup } from '@/lib/types';
import { InviteMembersModal } from './invite-members-modal';
import { ContributeModal } from './contribute-modal';
import { PendingContributions } from './pending-contributions';

interface GroupCardProps {
  group: SavingsGroup;
  memberCount?: number;
  userRole?: 'admin' | 'member';
  onView?: () => void;
  onManage?: () => void;
  onContribute?: () => void;
  onInviteMembers?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function GroupCard({ group, memberCount = 0, userRole = 'member', onView }: GroupCardProps) {
  const progress = group.targetAmount > 0 ? Math.min(100, (group.currentAmount / group.targetAmount) * 100) : 0;
  const remaining = Math.max(0, group.targetAmount - group.currentAmount);

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    paused: 'bg-amber-100 text-amber-700',
  };

  return (
    <Card className="flex flex-col rounded-2xl shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base truncate">{group.name}</CardTitle>
            {group.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{group.description}</p>
            )}
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 capitalize ${statusColors[group.status] ?? 'bg-slate-100 text-slate-600'}`}>
            {group.status}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 flex-1">
        {/* Progress */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-bold">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Amounts */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs text-muted-foreground">Saved</p>
            <p className="text-sm font-bold text-emerald-600 mt-0.5">{formatCurrency(group.currentAmount)}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs text-muted-foreground">Remaining</p>
            <p className="text-sm font-bold mt-0.5">{formatCurrency(remaining)}</p>
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1">
            <Target className="h-3.5 w-3.5" />
            {formatCurrency(group.targetAmount)}
          </span>
          {group.deadline && (
            <span>Due {new Date(group.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-3 border-t border-border flex-wrap">
        <ContributeModal group={group}>
          <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Contribute
          </Button>
        </ContributeModal>

        {userRole === 'admin' && (
          <>
            <InviteMembersModal groupId={group.id} groupName={group.name} group={group}>
              <Button size="sm" variant="outline">
                <Users className="h-3.5 w-3.5 mr-1.5" /> Invite
              </Button>
            </InviteMembersModal>
            <PendingContributions group={group} />
          </>
        )}
      </CardFooter>
    </Card>
  );
}
